"use client";
import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input, Button, message } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import chooseImage from "../../../assets/icons/tswira.png";
import {
  verifyResetCode,
  verifyTwoFA,
  resendTwoFactorCode,
  requestPasswordReset,
  
} from "../../../authPatient/redux/thunks/authThunks";
import "./VerifyCode.css";
import "../SignupPatient/SignupPatient.css";
import icon1 from "../../../assets/images/logomedilink.svg";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";

// doit correspondre à la durée de transition CSS (0.5s)
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";
// layout d'origine de cette page : form à gauche, hero à droite (pas de classe signup-doctor)
const DEFAULT_HERO_ON_LEFT = false;

// Découpe un titre en "reste du texte" + "dernier mot en accent"
const renderAccentTitle = (text) => {
  const words = text.split(" ");
  const lastWord = words.pop();
  return (
    <>
      {words.length > 0 ? words.join(" ") + " " : ""}
      <span className="signup-form-title-accent">{lastWord}</span>
    </>
  );
};

const VerifyCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const storedChallenge = (() => {
    try {
      const challenge = JSON.parse(
        sessionStorage.getItem("patientTwoFactorChallenge") || "null",
      ) || {};
      if (
        challenge.createdAt &&
        Date.now() - Number(challenge.createdAt) > 15 * 60 * 1000
      ) {
        sessionStorage.removeItem("patientTwoFactorChallenge");
        return {};
      }
      return challenge;
    } catch {
      return {};
    }
  })();
  const email     = location.state?.email     || storedChallenge.email || "";
  const userId    = location.state?.userId    || storedChallenge.userId || "";
  const tempToken = location.state?.tempToken || "";
  const is2FA     = !!(tempToken || userId);

  const [code, setCode]               = useState(["", "", "", "", "", ""]);
  const [errors, setErrors]           = useState({});
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown]     = useState(0);
  const inputRefs = useRef([]);
  const verifyInFlight = useRef(false);

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? DEFAULT_HERO_ON_LEFT : stored === "true";
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour le swap des 2 cards (form <-> hero) ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const handleContinueAsDoctor = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/LoginDoctor", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown((s) => s - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInputChange = (i, v) => {
    if (!/^\d*$/.test(v)) return;
    const next = [...code];
    next[i] = v;
    setCode(next);
    setErrors({});
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if ((e.key === "Backspace" || e.key === "Delete") && !code[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    const next = [...code];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];

    setCode(next);
    setErrors({});
    inputRefs.current[Math.min(pasted.length - 1, 5)]?.focus();
  };

  const validateCode = () => {
    const full = code.join("");
    if (full.length !== 6) {
      setErrors({ code: t("auth.sixDigitCodeIncomplete") });
      return false;
    }
    return true;
  };

  const handleVerify = async () => {
    if (verifyInFlight.current || !validateCode()) return;
    if (is2FA && !userId && !tempToken) {
      setErrors({ code: t("auth.verificationFailed") });
      return;
    }
    verifyInFlight.current = true;
    const fullCode = code.join("");

    try {
      // CASE 1: 2FA LOGIN
      if (is2FA) {
        const payload = tempToken
          ? { tempToken, code: fullCode }
          : { userId,    code: fullCode };

        const result = await dispatch(verifyTwoFA(payload)).unwrap();

        const id          = result?.user?.id || userId;
        const accessToken = result?.accessToken;

        if (accessToken) Cookies.set("accessToken", accessToken);
        if (id)          Cookies.set("id", String(id));
        sessionStorage.removeItem("patientTwoFactorChallenge");

        message.success({ content: t("auth.twoFASuccess"), duration: 2 });
        navigate("/feed", { replace: true });
        return;
      }

      // CASE 2: PASSWORD RESET FLOW
      await dispatch(verifyResetCode({ email, resetCode: fullCode })).unwrap();
      message.success({ content: t("auth.codeVerified"), duration: 2 });
      navigate("/ResetPassword", {
        state: { email, resetCode: fullCode, tempToken },
        replace: true,
      });

    } catch (err) {
      const errorMessage =
        typeof err === "string"
          ? err
          : err?.message || t("auth.verificationFailed");
      message.error({ content: errorMessage, duration: 3 });
      setErrors({ code: errorMessage });
    } finally {
      verifyInFlight.current = false;
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && code.join("").length === 6) handleVerify();
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      if (is2FA) {
        // Flow 2FA → resend OTP par userId
        if (!userId) {
          message.error({ content: t("auth.cannotResendMissingUser"), duration: 3 });
          return;
        }
        const res = await dispatch(resendTwoFactorCode({ userId })).unwrap();
        const msg = res?.message || t("auth.resendCodeSuccess");
        setCode(["", "", "", "", "", ""]);
        setErrors({});
        inputRefs.current[0]?.focus();
        message.success({ content: msg, duration: 3 });
      } else {
        // Flow reset password → renvoyer l'email de reset
        if (!email) {
          message.error({ content: t("auth.cannotResendMissingEmail"), duration: 3 });
          return;
        }
        await dispatch(requestPasswordReset(email)).unwrap();
        message.success({ content: t("auth.resendCodeSuccess"), duration: 3 });
      }
      setCountdown(60);
    } catch (err) {
      const errMsg =
        typeof err === "string" ? err : err?.message || t("auth.resendCodeFailed");
      message.error({ content: errMsg, duration: 4 });
      setErrors((s) => ({ ...s, resend: errMsg }));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="signup-container signup-patient-auth">
      {/* ── Back to Home Button (Fixed) ── */}
      <button
        type="button"
        className="signup-back-home signup-back-home-patient"
        onClick={() => navigate(ROUTES.LANDING)}
        aria-label={t("common.backToHome", "Back to home page")}
      >
        <ArrowLeftOutlined />
        <span>{t("common.backToHome", "Back to home page")}</span>
      </button>

      <div
        className={`signup-background ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >

        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form" onKeyDown={onKeyDown}>
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {renderAccentTitle(t("auth.verifyCode"))}
              </h1>
              <p className="signup-form-subtitle">
                {is2FA ? (
                  t("auth.verifyAccountSubtitleLogin2FA")
                ) : (
                  <>
                    {t("auth.verifyCodeSentEmail")} <strong>{email}</strong>
                  </>
                )}
              </p>
            </div>

            <div className="signup-form-content">

            <div className="signup-form-group">
              <label className="signup-form-label">{t("auth.verifyCode")}</label>
              <div className="vc-code-inputs">
                {code.map((digit, i) => (
                  <Input
                    key={i}
                    ref={(el) => (inputRefs.current[i] = el)}
                    value={digit}
                    onChange={(e) => handleInputChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={i === 0 ? handlePaste : undefined}
                    maxLength={1}
                    className={`vc-code signup-form-input ${errors.code ? "vc-error" : ""}`}
                    placeholder="0"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                ))}
              </div>

              {errors.code && (
                <span className="vc-error-message">{errors.code}</span>
              )}
            </div>

            <Button
              onClick={handleVerify}
              className="signup-create-account-btn"
              disabled={code.join("").length !== 6}
              type="primary"
            >
              <span>{t("auth.verifyCode")}</span>
              <ArrowRightOutlined />
            </Button>

            <div className="vc-resend-section">
              <p className="vc-resend-text">{t("auth.didntReceiveCode")}</p>
              <Button
                type="link"
                disabled={countdown > 0 || isResending}
                className="vc-resend-btn"
                onClick={handleResend}
              >
                {isResending
                  ? t("auth.sending")
                  : countdown > 0
                  ? t("auth.resendInSeconds", { count: countdown })
                  : <>
                      {t("auth.resendCode", "Resend Code").split(" ").map((word, idx, arr) => 
                        word.toLowerCase() === "code" ? 
                          <span key={idx} style={{ color: "rgba(15, 66, 177, 1)" }}>{word}</span> : 
                          <span key={idx}>{word}{idx < arr.length - 1 ? " " : ""}</span>
                      )}
                    </>
                }
              </Button>
            </div>

            <div className="vc-back-login">
              <Button
                type="link"
                onClick={() => navigate("/LoginPatient")}
                className="signup-login-link"
              >
                ← {t("auth.backToLogin", "Back to Login").split(" ").map((word, idx, arr) => 
                  word.toLowerCase() === "login" ? 
                    <span key={idx} style={{ color: "rgba(15, 66, 177, 1)" }}>{word}</span> : 
                    <span key={idx}>{word}{idx < arr.length - 1 ? " " : ""}</span>
                )}
              </Button>
            </div>
          </div>
        </div>
        </section>

        <section
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-top-icon" aria-hidden="true">
            <img src={icon1} alt="" />
          </div>
          <div className="signup-hero-inner">
            <h1 className="signup-hero-title">
              {t("signup.heroPanelTitle", "Are you joining as a healthcare professional?")}
            </h1>
            <p className="signup-hero-sub">
              {t("signup.heroPanelText", "This registration is designed for patients. If you are a doctor and wish to manage your practice, schedule appointments, and connect with patients, please create a doctor account.")}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsDoctor}
            >
              {t("signup.continueAsDoctor", "Continue as a doctor")}
              <ArrowRightOutlined />
            </button>
          </div>
        </section>

      </div>
    </div>
  );
};

export default VerifyCode;